import type { DeckCard } from "@/types/cards";
import type { Equipment, PlayerState } from "@/types/game";
import type { CardCatalogEntry } from "@/types/cards";

// 装备槽类型识别：根据 catalog subkind 判断
export function equipSlotOf(card: DeckCard): keyof Equipment | null {
  if (card.kind !== "装备牌") return null;
  switch (card.subkind) {
    case "武器":
      return "weapon";
    case "防具":
      return "armor";
    case "饰品":
      return "accessory";
    default:
      return null;
  }
}

// 把 catalog 上的装备数值加成写入 deck card 实例（装备时使用）
export function attachCatalogBonuses(
  card: DeckCard,
  catalog: CardCatalogEntry
): DeckCard {
  return {
    ...card,
    rangeBonus: catalog.rangeBonus,
    attackBonus: catalog.attackBonus,
    defenseBonus: catalog.defenseBonus,
    magicDefenseBonus: catalog.magicDefenseBonus,
    hpBonus: catalog.hpBonus,
    equipHeal: catalog.equipHeal,
    spellDamageBonus: catalog.spellDamageBonus,
    handLimitBonus: catalog.handLimitBonus,
    manaCostReduction: catalog.manaCostReduction,
  };
}

// 装备：把新卡放入对应槽，旧卡回弃牌堆
// 同步维护 maxHp（魔血戒指生命上限加成）与装备即时回血
export function equip(
  player: PlayerState,
  card: DeckCard,
  catalog: CardCatalogEntry
): {
  player: PlayerState;
  oldCard: DeckCard | undefined;
  healApplied: number; // 装备即时恢复实际生效的生命量（用于发 heal 事件）
} {
  const slot = equipSlotOf(card);
  if (!slot) {
    throw new Error(`不是装备牌: ${card.name}`);
  }
  const enhanced = attachCatalogBonuses(card, catalog);
  const oldCard = player.equip[slot];
  // 生命上限：卸下旧装的加成，装上新装的加成
  const oldHpBonus = oldCard?.hpBonus ?? 0;
  const newHpBonus = enhanced.hpBonus ?? 0;
  const maxHp = player.maxHp - oldHpBonus + newHpBonus;
  // 先按新上限截断（换下魔血戒指时可能超上限）
  let hp = Math.min(player.hp, maxHp);
  // 装备即时恢复（魔血戒指+20，不超过新上限）
  const healApplied = Math.min(enhanced.equipHeal ?? 0, Math.max(0, maxHp - hp));
  hp += healApplied;
  const newPlayer: PlayerState = {
    ...player,
    maxHp,
    hp,
    equip: { ...player.equip, [slot]: enhanced },
    // 攻击/防御/魔御读取由 damage.ts 通过 equip 槽获取
  };
  return { player: newPlayer, oldCard, healApplied };
}

// 卸下装备（一般是换装时旧装备进弃牌堆）
export function unequip(
  player: PlayerState,
  slot: keyof Equipment
): { player: PlayerState; oldCard: DeckCard | undefined } {
  const oldCard = player.equip[slot];
  const hpBonus = oldCard?.hpBonus ?? 0;
  const maxHp = player.maxHp - hpBonus;
  const newPlayer: PlayerState = {
    ...player,
    maxHp,
    hp: Math.min(player.hp, maxHp),
    equip: { ...player.equip, [slot]: undefined },
  };
  return { player: newPlayer, oldCard };
}

// 清空全部装备（死亡/重生）：回收所有生命上限加成
export function stripAllEquipment(player: PlayerState): {
  player: PlayerState;
  cards: DeckCard[];
} {
  const cards = [
    player.equip.weapon,
    player.equip.armor,
    player.equip.accessory,
  ].filter((c): c is DeckCard => Boolean(c));
  if (cards.length === 0) {
    return { player: { ...player, equip: {} }, cards: [] };
  }
  const hpBonus = cards.reduce((sum, c) => sum + (c.hpBonus ?? 0), 0);
  const maxHp = player.maxHp - hpBonus;
  return {
    player: {
      ...player,
      maxHp,
      equip: {},
    },
    cards,
  };
}

// 装备提供的 attackBonus 总和（来自武器+防具+饰品）
export function equipAttackBonus(p: PlayerState): number {
  return (
    (p.equip.weapon?.attackBonus ?? 0) +
    (p.equip.armor?.attackBonus ?? 0) +
    (p.equip.accessory?.attackBonus ?? 0)
  );
}

export function equipDefenseBonus(p: PlayerState): number {
  return (
    (p.equip.armor?.defenseBonus ?? 0) +
    (p.equip.accessory?.defenseBonus ?? 0)
  );
}

export function equipMagicDefenseBonus(p: PlayerState): number {
  return (
    (p.equip.armor?.magicDefenseBonus ?? 0) +
    (p.equip.accessory?.magicDefenseBonus ?? 0)
  );
}

// 装备提供的法术伤害加成总和（来自武器+饰品）
export function equipSpellDamageBonus(p: PlayerState): number {
  return (
    (p.equip.weapon?.spellDamageBonus ?? 0) +
    (p.equip.accessory?.spellDamageBonus ?? 0)
  );
}

// 装备提供的手牌上限加成（记忆之戒+2，仅饰品）
export function equipHandLimitBonus(p: PlayerState): number {
  return p.equip.accessory?.handLimitBonus ?? 0;
}

// 实际手牌上限（基础 + 装备加成，动态计算无需装备/卸下时同步）
export function effectiveHandLimit(p: PlayerState): number {
  return p.handLimit + equipHandLimitBonus(p);
}

// 法术魔法消耗减免（法神戒指-5，仅饰品）
export function equipManaCostReduction(p: PlayerState): number {
  return p.equip.accessory?.manaCostReduction ?? 0;
}

// 金刚戒指：饰品槽佩戴金刚戒指时，武器/防具槽无法被缴械或掠夺；戒指自身（饰品槽）仍可被指定
export function isSlotProtectedByJingang(
  target: PlayerState,
  slot: keyof Equipment
): boolean {
  if (target.equip.accessory?.name !== "金刚戒指") return false;
  return slot !== "accessory";
}
